import { type FC } from "react";

import { Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import type { EvalCheckResult } from "@api/rest/types-and-hooks";
import {
  CHECK_STYLES,
  checkStatus,
  isCheckWorthShowing,
  metricLabel,
  ruleVerdict,
} from "@components/EvalReview/constants";

interface RuleCheckListProps {
  checks: EvalCheckResult[];
  onHighlight: (quotes: string[]) => void;
}

/**
 * The deterministic checks, as findings rather than test results.
 *
 * Each evaluates a closed set — our own placeholder vocabulary, published
 * score bands — so its verdict is provable. None of them says anything about
 * whether the output is *good*.
 *
 * A table sized to its content, so the check names decide the first column's
 * width instead of wrapping inside a fixed one. `w-auto` keeps the verdicts
 * tucked against the longest name rather than stretched across the panel.
 *
 * The verdict is a green or red chip — it is a pass/fail status, and reading
 * it as one should not require reading the words. A failure gets its reason
 * underneath, because that is the part worth reading.
 */
export const RuleCheckList: FC<RuleCheckListProps> = ({ checks: allChecks, onHighlight }) => {
  const checks = allChecks.filter(isCheckWorthShowing);
  if (checks.length === 0) return null;

  return (
    <section className="py-4">
      <Label size="small" colorType="primary" className="uppercase tracking-wide">
        Checks
      </Label>
      <table className="mt-2 w-auto border-separate border-spacing-0 text-sm">
        <tbody>
          {checks.map((check) => {
            const failed = check.passed === false;
            const verdict = ruleVerdict(check);

            return (
              <tr key={check.metric_name}>
                <th
                  scope="row"
                  className="whitespace-nowrap py-1 pr-6 text-left align-top font-normal text-text-secondary"
                >
                  {metricLabel(check.metric_name)}
                </th>
                <td className="py-1 align-top">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    {/* The verdict is the status, so it carries the colour as
                        a chip — same shape, size and weight as the run's own
                        status chip in PromptTestBed, since they are the same
                        kind of thing. */}
                    <span className={`shrink-0 rounded px-2 py-0.5 text-sm ${CHECK_STYLES[checkStatus(check.passed)]}`}>
                      {verdict}
                    </span>
                    {check.spans.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => onHighlight(check.spans)}
                        className="cursor-pointer text-sm text-rio-700 underline decoration-dotted underline-offset-2 hover:text-rio-800"
                      >
                        show {check.spans.length} in the output
                      </button>
                    ) : null}
                  </div>
                  {(failed || check.passed === null) && check.reason ? (
                    <Paragraph displayType="normal" size="small" className="mt-1 text-text-secondary">
                      {check.reason}
                    </Paragraph>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
};
